/**
 * Pengaturan tampilan peta (localStorage).
 */

import { ensureTierPalette, ensureManualBreaks } from './mapTierUtils';

export const MAP_SETTINGS_STORAGE_KEY = 'tulufali_map_customization_v1';
export const MAP_SETTINGS_CHANGED_EVENT = 'tulufali-map-settings-changed';

const DEFAULT_TIER_PALETTE = ['#90caf9', '#42a5f5', '#1976d2', '#0d47a1'];

export const DEFAULT_MAP_SETTINGS = {
  colors: {
    none: '#e8e8e8',
    low: '#90caf9',
    medium: '#42a5f5',
    high: '#1976d2',
    highest: '#0d47a1',
    stroke: '#ffffff',
    hover: '#1565c0',
    kecamatanLabel: '#1a1a1a',
  },
  showKecamatanLabels: false,
  /** 'desa' | 'kecamatan' */
  mapLevel: 'desa',
  /** 'csv' = hanya data upload (default awal kosong); 'api' = DTSEN dari server */
  dataSource: 'csv',
  /** Jumlah kelompok gradasi (min 2, max 8), tidak termasuk "tidak ada data" */
  tierCount: 4,
  /** 'quantile' | 'manual' */
  tierMode: 'quantile',
  /** Batas atas per kelompok (panjang = tierCount - 1), naik. Hanya dipakai jika tierMode manual */
  manualTierBreaks: [10, 25, 50],
  /** Warna per tingkat gradasi, panjang = tierCount */
  tierPalette: [...DEFAULT_TIER_PALETTE],
  /** Warna manual per wilayah: kunci "desa:NORMALIZED" atau "kec:NORMALIZED" */
  polygonManualColors: {},
  /**
   * Mode per kecamatan: null = semua kecamatan dari geometri ditampilkan bergradasi.
   * Array kunci ternormalisasi = hanya kecamatan ini yang bergradasi; lainnya abu "tidak ada data".
   */
  kecamatanIncludedKeys: null,
};

function cloneSettings(base) {
  return {
    ...base,
    colors: { ...base.colors },
    tierPalette: [...(base.tierPalette || DEFAULT_TIER_PALETTE)],
    manualTierBreaks: [...(base.manualTierBreaks || [])],
    polygonManualColors: { ...(base.polygonManualColors || {}) },
    kecamatanIncludedKeys:
      base.kecamatanIncludedKeys == null
        ? null
        : Array.isArray(base.kecamatanIncludedKeys)
          ? [...base.kecamatanIncludedKeys]
          : null,
  };
}

function migrateLegacy(parsed) {
  const p = { ...parsed };
  if (!p.tierPalette || !Array.isArray(p.tierPalette) || p.tierPalette.length < 2) {
    p.tierPalette = ensureTierPalette(
      [
        p.colors?.low,
        p.colors?.medium,
        p.colors?.high,
        p.colors?.highest,
      ].filter(Boolean),
      p.tierCount || 4,
      DEFAULT_TIER_PALETTE
    );
  }
  const nT = Math.max(2, Math.min(8, Number(p.tierCount) || 4));
  p.tierCount = nT;
  p.tierPalette = ensureTierPalette(p.tierPalette, nT, DEFAULT_TIER_PALETTE);
  p.manualTierBreaks = ensureManualBreaks(p.manualTierBreaks, nT);
  if (!p.polygonManualColors || typeof p.polygonManualColors !== 'object') p.polygonManualColors = {};
  if (p.mapLevel !== 'desa' && p.mapLevel !== 'kecamatan') p.mapLevel = 'desa';
  if (p.dataSource !== 'api' && p.dataSource !== 'csv') p.dataSource = 'csv';
  if (p.tierMode !== 'manual' && p.tierMode !== 'quantile') p.tierMode = 'quantile';
  if (p.kecamatanIncludedKeys != null && !Array.isArray(p.kecamatanIncludedKeys)) {
    p.kecamatanIncludedKeys = null;
  }
  return p;
}

export function normalizeMapSettings(partial) {
  if (partial == null) return cloneSettings(DEFAULT_MAP_SETTINGS);
  const merged = {
    ...DEFAULT_MAP_SETTINGS,
    ...partial,
    colors: { ...DEFAULT_MAP_SETTINGS.colors, ...(partial.colors || {}) },
    tierPalette: ensureTierPalette(
      partial.tierPalette,
      partial.tierCount || DEFAULT_MAP_SETTINGS.tierCount,
      DEFAULT_TIER_PALETTE
    ),
    manualTierBreaks: ensureManualBreaks(
      partial.manualTierBreaks,
      partial.tierCount || DEFAULT_MAP_SETTINGS.tierCount
    ),
    polygonManualColors: { ...(partial.polygonManualColors || {}) },
    kecamatanIncludedKeys:
      partial.kecamatanIncludedKeys == null
        ? null
        : Array.isArray(partial.kecamatanIncludedKeys)
          ? [...partial.kecamatanIncludedKeys]
          : null,
  };
  const nT = Math.max(2, Math.min(8, Number(merged.tierCount) || 4));
  merged.tierCount = nT;
  merged.tierPalette = ensureTierPalette(merged.tierPalette, nT, DEFAULT_TIER_PALETTE);
  merged.manualTierBreaks = ensureManualBreaks(merged.manualTierBreaks, nT);
  return merged;
}

export function loadMapSettings() {
  try {
    const raw = localStorage.getItem(MAP_SETTINGS_STORAGE_KEY);
    if (!raw) return cloneSettings(DEFAULT_MAP_SETTINGS);
    const parsed = JSON.parse(raw);
    return normalizeMapSettings(migrateLegacy(parsed));
  } catch {
    return cloneSettings(DEFAULT_MAP_SETTINGS);
  }
}

export function saveMapSettings(settings) {
  const merged = normalizeMapSettings(settings);
  localStorage.setItem(MAP_SETTINGS_STORAGE_KEY, JSON.stringify(merged));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(MAP_SETTINGS_CHANGED_EVENT));
  }
}

export function resetMapSettings() {
  localStorage.removeItem(MAP_SETTINGS_STORAGE_KEY);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(MAP_SETTINGS_CHANGED_EVENT));
  }
}
